import { useMemo } from 'react';
import { useFragment, graphql } from 'react-relay';
import type { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import {
    useSplit_address$key,
    useSplit_address$data,
} from './__generated__/useSplit_address.graphql';
import {
    useSplit_paymentAddresses$key,
    useSplit_paymentAddresses$data,
} from './__generated__/useSplit_paymentAddresses.graphql';

export type Allocation = NonNullable<
    ArrayElement<NonNullable<NonNullable<useSplit_address$data['split']>['allocations']>>
>;

type SplitData = {
    normalizedAllocations: Allocation[];
    splitName?: string | null;
    paymentAddress?: string | null;
    paymentEns?: string | null;
    isOxSplit: boolean;
    splitDetails?: null; // 0xSplit information only available for certain splits
    distributorFee?: string | null;
    controllerAddress?: string | null;
    controllerEns?: string | null;
};

function deriveHasSplit(
    singleAddress?: useSplit_address$data | null,
    addressList?: useSplit_paymentAddresses$data | null
): boolean {
    return (
        !!singleAddress?.split ||
        !!addressList?.addresses?.[0]?.address?.split ||
        (addressList?.addresses?.length || 0) > 1
    );
}
function deriveSplitFromSingleAddress(singleAddress: useSplit_address$data): SplitData {
    const { address: addressString, ensDomain } = singleAddress;
    const splitName = singleAddress.split?.bookmark?.name;
    const distributorFee = singleAddress.split?.distributorFee;
    const controllerAddress = singleAddress.split?.controllerAddress?.address;
    const controllerEns = singleAddress.split?.controllerAddress?.ensDomain;
    const paymentAddress = addressString;
    const paymentEns = ensDomain;
    const normalizedAllocations = singleAddress.split?.allocations
        ? [...singleAddress.split.allocations]
        : [
              {
                  allocation: 10000,
                  basisPoints: 10000,
                  address: { ...singleAddress },
              },
          ];
    return {
        splitName,
        distributorFee,
        controllerAddress,
        controllerEns,
        paymentAddress,
        paymentEns,
        normalizedAllocations: normalizedAllocations.filter(filterNulls),
        isOxSplit: !!singleAddress.split,
    };
}
function deriveSplitFromAddressList(addressList: useSplit_paymentAddresses$data): SplitData {
    const firstAddress = addressList.addresses?.[0];
    let normalizedAllocations: Allocation[] = [];
    let splitName: string | null | undefined;
    let distributorFee: string | null | undefined;
    let controllerAddress: string | null | undefined;
    let controllerEns: string | null | undefined;
    let paymentAddress: string | null | undefined;
    let paymentEns: string | null | undefined;
    let isOxSplit = false;
    if (addressList.addresses?.length === 1 && firstAddress?.address?.split?.allocations) {
        const { address: addressString, ensDomain, split } = firstAddress.address;
        paymentAddress = addressString;
        paymentEns = ensDomain;
        distributorFee = split.distributorFee;
        controllerAddress = split.controllerAddress?.address;
        controllerEns = split.controllerAddress?.ensDomain;
        splitName = split.bookmark?.name;
        normalizedAllocations = [...firstAddress.address.split.allocations].filter(filterNulls);
        isOxSplit = true;
    } else if (addressList.addresses) {
        if (addressList.addresses.length === 1) {
            paymentAddress = firstAddress?.address?.address || '';
            paymentEns = firstAddress?.address?.ensDomain || '';
            distributorFee = firstAddress?.address?.split?.distributorFee;
            controllerAddress = firstAddress?.address?.split?.controllerAddress?.address;
            controllerEns = firstAddress?.address?.split?.controllerAddress?.ensDomain;
        }
        let totalProfitAllocated = 0;
        addressList.addresses.forEach(address => {
            totalProfitAllocated += address?.allocation || 0;
        });
        normalizedAllocations = addressList.addresses.map(address => ({
            allocation:
                ((address?.allocation || 0) / totalProfitAllocated) *
                (address?.basisPoints || 10000),
            address: address?.address ? { ...address.address } : null,
            basisPoints: address?.basisPoints || 10000,
        }));
    }
    return {
        splitName,
        paymentAddress,
        paymentEns,
        normalizedAllocations,
        isOxSplit,
        distributorFee,
        controllerAddress,
        controllerEns,
    };
}

export const useSplit = ({
    singleAddress: singleAddressRef = null,
    addressList: addressListRef = null,
}: {
    singleAddress?: useSplit_address$key | null;
    addressList?: useSplit_paymentAddresses$key | null;
}): SplitData & { hasSplit: boolean } => {
    const singleAddress = useFragment(
        graphql`
            fragment useSplit_address on EthereumAddressType {
                address
                ensDomain
                publicProfile {
                    displayName
                    avatarImage {
                        path
                    }
                }
                split {
                    bookmark(userId: $userId) @include(if: $fetchSplitName) {
                        name
                    }
                    distributorFee
                    controllerAddress {
                        address
                        ensDomain
                    }
                    allocations {
                        allocation
                        basisPoints
                        address {
                            address
                            ensDomain
                            publicProfile {
                                displayName
                                avatarImage {
                                    path
                                }
                            }
                        }
                    }
                }
            }
        `,
        singleAddressRef
    );
    const addressList = useFragment(
        graphql`
            fragment useSplit_paymentAddresses on PaymentAddresses {
                addresses {
                    address {
                        address
                        ensDomain
                        publicProfile {
                            displayName
                            avatarImage {
                                path
                            }
                        }
                        split {
                            bookmark(userId: $userId) @include(if: $fetchSplitName) {
                                name
                            }
                            distributorFee
                            controllerAddress {
                                address
                                ensDomain
                            }
                            allocations {
                                allocation
                                basisPoints
                                address {
                                    address
                                    ensDomain
                                    publicProfile {
                                        displayName
                                        avatarImage {
                                            path
                                        }
                                    }
                                }
                            }
                        }
                    }
                    allocation
                    basisPoints
                }
            }
        `,
        addressListRef
    );
    const splitData = useMemo(() => {
        const hasSplit = deriveHasSplit(singleAddress, addressList);
        if (singleAddress) {
            return { hasSplit, ...deriveSplitFromSingleAddress(singleAddress) };
        } else if (addressList) {
            return { hasSplit, ...deriveSplitFromAddressList(addressList) };
        }
        return { hasSplit, normalizedAllocations: [], isOxSplit: false };
    }, [singleAddress, addressList]);

    return splitData;
};
